/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  Divider,
  Heading,
  Link,
  useDialogContainer
} from '@adobe/react-spectrum';
import {FormattedMessage, injectIntl} from 'react-intl';
import * as intlMessages from './__localization__/ShellLegalNoticesDialog';
import {IntlProvider} from '@quarry/intl';
import React from 'react';
import '../ShellHeader.scss';
import '../ShellHeaderActions.scss';

const ShellLegalNoticesDialog = ({intl}) => {
  const dialog = useDialogContainer();
  return (
    <Dialog UNSAFE_className="spectrum-legalDialog" size="S">
      <Heading>
        <FormattedMessage
          defaultMessage="Legal notices"
          description="Label for Legal notices menu item"
          id="profileLegalNoticesMenuItem"
        />
      </Heading>
      <Divider />
      <Content>
        <div>
          <Link>
            <a
              href={intl.formatMessage({
                defaultMessage: 'https://www.adobe.com/go/experiencecloudterms',
                description: 'GoUrl for Adobe Experience Cloud Terms of Use',
                id: 'legalTermsOfUseLink'
              })}
              rel="noreferrer"
              target="_blank"
            >
              <FormattedMessage
                defaultMessage="Terms of Use"
                description="Link for Terms of Use"
                id="legalTermsOfUse"
              />
            </a>
          </Link>
        </div>
        <div>
          <Link>
            <a href="https://www.adobe.com/privacy/policy.html" rel="noreferrer" target="_blank">
              <FormattedMessage
                defaultMessage="Privacy Policy"
                description="Link for Privacy Policy"
                id="legalPrivacyPolicy"
              />
            </a>
          </Link>
        </div>
        <div>
          <p>
            <FormattedMessage
              defaultMessage="Copyright © {copyrightYears} Adobe Inc. All rights reserved."
              description="Legal modal copyright"
              id="legalCopyright"
              values={{copyrightYears: `2019-${new Date().getFullYear()}`}}
            />
          </p>
        </div>
      </Content>
      <ButtonGroup>
        <Button data-testid="legal-dialog-close-button" onPress={dialog.dismiss} variant="secondary">
          <FormattedMessage
            defaultMessage="Close"
            description="Legal modal confirm label"
            id="legalConfirmLabel"
          />
        </Button>
      </ButtonGroup>
    </Dialog>
  );
};

const withI18nProvider = Component => function LocalizedComponent(props) {
  return (
    <IntlProvider locale={props.locale} messages={intlMessages}>
      <Component
        {...props}
      />
    </IntlProvider>
  );
};

export default withI18nProvider(injectIntl(ShellLegalNoticesDialog));
